import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { WORKFLOW_ACTION } from '../entities/workflow/workflow'
import { optionalString, requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de flujos de trabajo. Décimo y último catálogo
 * del backoffice en v2, y el único que NO es un catálogo de filas sueltas: un
 * flujo define la **máquina de estados** por la que camina una negociación, así
 * que su dato central es una secuencia ORDENADA de estados.
 *
 * `wfl_` no es `flw_`. Ese otro prefijo pertenece al motor de flujos del portal
 * del cliente (`FlowDefinition`): dos entidades que en español se leen «flujo» y
 * no comparten ni colección ni contrato.
 *
 * **La decisión de diseño de esta tajada**: los pasos ENTRAN como un array plano
 * de `sta_` y SALEN resueltos con su `order`. El v1 acepta `{ state, order }` y
 * después descarta el `order` que recibió, recalculándolo desde la posición del
 * array —un campo que el cliente puede mandar y que el servidor ignora es una
 * mentira del contrato: invita a creer que se puede reordenar mandando otro
 * número—. En v2 el orden ES la posición, así que no hay nada que mandar: mover
 * un paso es mover el elemento del array. El `order` sigue existiendo en la
 * salida porque la UI lo muestra («Paso 1, Paso 2»), pero es derivado.
 *
 * `name` es único GLOBAL entre flujos vivos (case-insensitive); un duplicado
 * responde `409 ERROR_WORKFLOW_EXIST`.
 */

/**
 * Autoría wire-safe: el `_id` del usuario no cruza el adapter, así que se publica su
 * `usr_`. Va entera nullable porque el legacy guarda `createdBy: null` en las filas
 * sembradas, y la UI hace mucho que resuelve ese caso a «—».
 */
export const V2AdminWorkflowAuthorSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string()
  })
  .nullable()

/**
 * Estado del paso, resuelto para mostrar. Nullable por robustez: un flujo que
 * referencia un estado borrado —o uno que perdió su `publicId`— presenta el paso
 * con `state: null` en vez de tumbar el listado entero. El borrado de un estado
 * hace cascada sobre `workflow.states`, así que en la práctica no debería pasar.
 */
export const V2AdminWorkflowStepStateSchema = z
  .object({
    id: PublicIdSchemas.state,
    name: z.string(),
    color: z.string().nullable(),
    type: z.string()
  })
  .nullable()

/** Paso del flujo en el wire de SALIDA: el `order` es derivado de la posición. */
export const V2AdminWorkflowStepSchema = z.object({
  /** Derivado de la posición en el array (1-based). No se acepta en la entrada. */
  order: z.number().int().positive(),
  state: V2AdminWorkflowStepStateSchema
})

/** Fila del listado y del detalle. */
export const V2AdminWorkflowSchema = z.object({
  id: PublicIdSchemas.workflow,
  name: z.string(),
  description: z.string(),
  /** Secuencia ORDENADA de pasos. El índice del array es la verdad del orden. */
  states: z.array(V2AdminWorkflowStepSchema),
  actions: z.array(z.nativeEnum(WORKFLOW_ACTION)),
  /** Hay a lo sumo un flujo predeterminado POR CONJUNTO DE ACCIONES. */
  default: z.boolean(),
  active: z.boolean(),
  createdBy: V2AdminWorkflowAuthorSchema,
  updatedBy: V2AdminWorkflowAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminWorkflowsPageSchema = z.object({
  data: z.array(V2AdminWorkflowSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y facetas `active` y `default`, con los mismos filtros allowlist del
 * listado. Cada faceta ignora el filtro de su propia dimensión; `total` respeta la
 * consulta completa. `default` sí es faceta acá (a diferencia de otros catálogos):
 * es de baja cardinalidad y tiene valor de navegación real —«cuáles son los
 * predeterminados» es la pregunta que se hace sobre esta tabla—.
 */
export const V2AdminWorkflowsSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  }),
  default: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/** Entrada mínima del selector de flujos. */
export const V2AdminWorkflowLookupItemSchema = z.object({
  id: PublicIdSchemas.workflow,
  name: z.string()
})

export const V2AdminWorkflowLookupSchema = z.object({
  data: z.array(V2AdminWorkflowLookupItemSchema)
})

/**
 * Alta de flujo.
 *
 * `states` es un array PLANO de `sta_` en el orden de ejecución: el servidor
 * numera los pasos por su posición y valida que todos los estados existan
 * (`400 ERROR_STATE_NOT_FOUND`). Un flujo sin pasos no es una máquina, así que se
 * exige al menos uno.
 *
 * Reglas de dominio que el service aplica: la acción `new` es EXCLUSIVA (no se
 * combina con otras — `400 ERROR_CANNOT_ADD_MORE_OPTIONS_IF_CREATE_EXISTS`) y
 * marcar `default` desmarca al predeterminado anterior del MISMO conjunto de
 * acciones.
 */
export const V2CreateAdminWorkflowSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  description: optionalString(),
  states: z.array(PublicIdSchemas.state).min(1, 'Agrega al menos un estado al flujo'),
  actions: z.array(z.nativeEnum(WORKFLOW_ACTION)).min(1, 'Elige al menos una acción'),
  default: z.boolean().optional(),
  active: z.boolean().optional()
})

/**
 * Edición parcial: un id desconocido responde 404 (nunca hace upsert). Enviar
 * `states` REEMPLAZA la secuencia entera —es una lista ordenada, no un conjunto
 * al que se le hace merge—; omitirlo la conserva. El flujo predeterminado de una
 * acción no puede cambiar de acción (`400 ERROR_NOT_UPDATE_ACTION_DEFAULT`): la
 * dejaría sin predeterminado.
 */
export const V2UpdateAdminWorkflowSchema = V2CreateAdminWorkflowSchema.partial()

export type V2AdminWorkflowAuthor = z.infer<typeof V2AdminWorkflowAuthorSchema>
export type V2AdminWorkflowStepState = z.infer<typeof V2AdminWorkflowStepStateSchema>
export type V2AdminWorkflowStep = z.infer<typeof V2AdminWorkflowStepSchema>
export type V2AdminWorkflow = z.infer<typeof V2AdminWorkflowSchema>
export type V2AdminWorkflowsPage = z.infer<typeof V2AdminWorkflowsPageSchema>
export type V2AdminWorkflowsSummary = z.infer<typeof V2AdminWorkflowsSummarySchema>
export type V2AdminWorkflowLookupItem = z.infer<typeof V2AdminWorkflowLookupItemSchema>
export type V2AdminWorkflowLookup = z.infer<typeof V2AdminWorkflowLookupSchema>
export type V2CreateAdminWorkflowInput = z.infer<typeof V2CreateAdminWorkflowSchema>
export type V2UpdateAdminWorkflowInput = z.infer<typeof V2UpdateAdminWorkflowSchema>
